// React
import { ReactNode, useEffect, useRef } from 'react';

// MUI
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Components
import TemplateItem from '../../TemplateItem';
import { STEP_TYPES } from 'utils/templates';

interface SectionProps {
  section: any;
  progress?: {
    completed: number;
    total: number;
  };
  score?: {
    percentage: number;
    earned: number;
    total: number;
  };
  isShowScoreInProgress?: boolean;
  isMobileDeviceMd?: boolean;
  isLogSubmitted?: boolean;
  onResponseToItem?: any;
  updateMultipleItems?: any;
  automations?: any;
  childItemsMap?: any;
  lastUpdatedRef?: any;
  onNotePostToItem?: any;
  handleUpdateItemNote?: any;
  handleDeleteChecklistItemNote?: any;
  handleCreateCorrectiveTask?: any;
  itemErrors?: any;
  handleShowUploadModal?: any;
  isActionsAllowed?: boolean;
  taskDetail?: any;
  checkListLogHashes?: any;
  checkVisibility?: (item: any) => boolean;
  localChecklist?: any;
  isSectionVsibile: boolean;
}

const Section = ({
  section,
  progress = { completed: 0, total: 0 },
  score = { percentage: 0, earned: 0, total: 0 },
  isMobileDeviceMd = false,
  isLogSubmitted = false,
  onResponseToItem,
  updateMultipleItems,
  automations,
  childItemsMap,
  lastUpdatedRef,
  onNotePostToItem,
  handleUpdateItemNote,
  handleDeleteChecklistItemNote,
  handleCreateCorrectiveTask,
  itemErrors,
  handleShowUploadModal,
  isActionsAllowed = false,
  taskDetail = {},
  checkListLogHashes,
  checkVisibility = () => true,
  localChecklist,
  isSectionVsibile,
}: SectionProps) => {
  useEffect(() => {
    if (isSectionVsibile) {
      if (section.type === STEP_TYPES.HEADER) {
        updateMultipleItems?.(
          section?.items
            ? section?.items.reduce((ids, item) => {
                if (
                  item.type === STEP_TYPES.NUMBER ||
                  item.type === STEP_TYPES.TEMPERATURE ||
                  item.type === STEP_TYPES.COST ||
                  item.type === STEP_TYPES.CALCULATION
                ) {
                  ids.push(item.id);
                }
                return ids;
              }, [])
            : [],
          {
            isHidden: false,
          },
        );
      }
    } else {
      if (section.type === STEP_TYPES.HEADER) {
        updateMultipleItems?.(
          section?.items
            ? section?.items.reduce((ids, item) => {
                if (
                  item.type === STEP_TYPES.NUMBER ||
                  item.type === STEP_TYPES.TEMPERATURE ||
                  item.type === STEP_TYPES.COST ||
                  item.type === STEP_TYPES.CALCULATION
                ) {
                  ids.push(item.id);
                }
                return ids;
              }, [])
            : [],
          {
            isHidden: true,
          },
        );
      }
    }
  }, [isSectionVsibile]);
  return isSectionVsibile ? (
    <Accordion
      sx={{
        width: '100% !important',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
      }}
      TransitionProps={{ unmountOnExit: true }}
      defaultExpanded
      disableGutters
      square={false}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          background: '#EBEAFF',
          height: 'fit-content !important',
          minHeight: 'initial !important',
          borderRadius: '8px',
        }}
      >
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap={'wrap'}
          gap={isMobileDeviceMd ? '8px' : '0px'}
        >
          <Typography fontWeight={600} fontSize="18px" color="#000000DE">
            {section.description} ({progress?.completed || '0'} of{' '}
            {progress?.total || '0'})
          </Typography>
          {localChecklist?.Checklist?.isShowScoreInProgress && (
            <Chip
              label={`Score: ${score.percentage}% (${score.earned} of ${score.total} pts)`}
              size="small"
              sx={{
                background: '#CCCBFF',
                fontWeight: 700,
                fontSize: '13px',
              }}
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pb: 0 }}>
        <Stack spacing={2}>
          {section?.items?.map((item: any) => {
            const isVisible = checkVisibility(item);
            return (
              <Box
                key={item.id}
                ref={
                  item?.updatedAt === localChecklist?.lastItemUpdatedAt
                    ? lastUpdatedRef
                    : null
                }
              >
                <TemplateItem
                  showTimeStamp={localChecklist?.Checklist?.isShowTimestamp}
                  isLogSubmitted={isLogSubmitted}
                  onChangeCb={onResponseToItem}
                  updateMultipleItems={updateMultipleItems}
                  item={item}
                  automations={automations}
                  childItemsMap={childItemsMap}
                  lastUpdatedRef={lastUpdatedRef}
                  onNotePostToItem={onNotePostToItem}
                  handleUpdateItemNote={handleUpdateItemNote}
                  handleDeleteChecklistItemNote={handleDeleteChecklistItemNote}
                  handleCreateCorrectiveTask={handleCreateCorrectiveTask}
                  itemErrors={itemErrors}
                  handleShowUploadModal={handleShowUploadModal}
                  isActionsAllowed={isActionsAllowed}
                  taskLocation={taskDetail.LocationId}
                  checkListLogHashes={checkListLogHashes}
                  isItemVisibile={isVisible}
                  checkVisibility={checkVisibility}
                />
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  ) : null;
};

export default Section;
